import React, { useState } from "react";
import { Box, Divider, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { AxisBaseline, SelectionType, TickInterval } from "../chart/LineChart";
import TabbedDialog from "components/dialogs/TabbedDialog";
import HistogramAggSelector from "components/widgetParts/HistogramAggSelector";
import { Agg } from "system/query/Aggs";
import { getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions, { getWidgetSettingsDelta } from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import SettingsLegend, { WidgetLegendSettings } from "components/settingsConfig/uiComponents/SettingsLegend";
import SettingsSingleSelect from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import { defaultSettings, defaultWidgetRequest } from "../LineChartWidget";
import LineChartWidgetPreview from "./LineChartPreview";

export type LineChartSettingsState = {
  // Widget Title
  widgetTitleAlignment: string;
  widgetTitleCustom: string;
  widgetTitleSource: string;

  // Line
  alignStart: boolean;
  selectionType: SelectionType;
  showDots: boolean;
  showTrendEvents: boolean;

  // X-Axis
  showXAxis: boolean;
  xTickInterval: TickInterval;

  // Y-Axis
  showYAxis: boolean;
  yBaseline: AxisBaseline;
  yTickInterval: TickInterval;
} & WidgetLegendSettings;

export default function LineChartSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, "line-chart");
  const requestBody = originalRequest?.requestInit?.body || {};

  const axisTickIntervalOptions: Array<{label: string, value: TickInterval}> = [
    { label: i18n.t("widget-types.line-chart.settings.axis-tick-interval-options.first-last"), value: "firstLast" },
    { label: i18n.t("widget-types.line-chart.settings.axis-tick-interval-options.interval"), value: "interval" }
  ];
  const selectionTypeOptions: Array<{label: string, value: SelectionType}> = [
    { label: "Band", value: "band" },
    { label: "Point", value: "point" },
  ]
  const yBaselineOptions: Array<{label: string, value: AxisBaseline}> = [
    { label: i18n.t("widget-types.line-chart.settings.y-baseline-options.truncated"), value: "truncated" },
    { label: i18n.t("widget-types.line-chart.settings.y-baseline-options.zero"), value: "zero" }
  ];

  // UI Management
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [widgetSettings, setWidgetSettings] = useState<LineChartSettingsState>({
    ...defaultSettings,
    ...props.widget?.data?.settings,
    legendAlign: {
      ...defaultSettings.legendAlign,
      ...props.widget.data?.settings?.legendAlign
    }
  });
  // Query & Data
  const [localAggs, setLocalAggs] = useState<Agg>(requestBody?.aggs?.histogram);

  function handleSettingsSave(): void {
    const settingsDelta = getWidgetSettingsDelta(defaultSettings, widgetSettings);
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    widgetRequest.requestInit = {
      body: {
        aggs: {
          histogram: localAggs
        }
      }
    };
    props.saveWidgetDataAndRequests({
      settings: settingsDelta
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }

  return (
    <TabbedDialog
      currentTab={tabIndex}
      handleTabChange={(event, value) => setTabIndex(value)}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        handleSettingsSave();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widgets.settings.query-tab"), i18n.t("widgets.settings.display-tab")]}
      title={i18n.t("widget-types.line-chart.settings.title")}>
      <>
        {tabIndex === 0 && props.queryInfo &&
          <HistogramAggSelector
            aggs={localAggs}
            queryInfo={props.queryInfo}
            setAggs={(aggs:Agg) => setLocalAggs(aggs)}
          />
        }
        {tabIndex === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Graph Preview */}
            <Box sx={{width: "50%", height: "35vh", position: "sticky", top: 18}}>
              <LineChartWidgetPreview settings={widgetSettings} />
            </Box>

            {/* Settings Options */}
            <SettingsOptions
              onChange={(newState) => setWidgetSettings(newState)}
              settingsState={widgetSettings}
            >
              {/* Widget Title */}
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitleSource"
                  label={i18n.t("widget-settings.widget-title")}
                  showQueryOption={true}
                  widgetTitleValue={widgetSettings.widgetTitleSource ?? "default"}
                />
                {widgetSettings.widgetTitleSource === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="widgetTitleCustom"
                      label={i18n.t("widget-settings.custom-title")}
                      value={widgetSettings.widgetTitleCustom}
                    />
                  </Stack>
                }
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={widgetSettings.widgetTitleAlignment}
                />
              </SettingsAccordion>

              {/* Line */}
              <SettingsAccordion label={i18n.t("widget-types.line-chart.settings.section-lines")} showBottomDivider={true}>
                <SettingsCheckbox
                  checked={widgetSettings.alignStart}
                  keyid="alignStart"
                  label={i18n.t("widget-types.line-chart.settings.align-start")}
                />
                <SettingsSingleSelect
                  defaultValue={selectionTypeOptions[0]}
                  keyid="selectionType"
                  label="Selection Type"
                  options={selectionTypeOptions}
                  value={selectionTypeOptions.find((option) => option.value === widgetSettings.selectionType)}
                />
                <SettingsCheckbox
                  checked={widgetSettings.showDots}
                  keyid="showDots"
                  label={i18n.t("widget-types.line-chart.settings.show-dots")}
                />
                <SettingsCheckbox
                  keyid="showTrendEvents"
                  label={i18n.t("widget-types.line-chart.settings.show-trend-events")}
                  checked={widgetSettings.showTrendEvents}
                />
              </SettingsAccordion>

              {/* Y-Axis */}
              <SettingsAccordion label={i18n.t("widget-types.line-chart.settings.section-x-axis")} showBottomDivider={true}>
                <SettingsCheckbox
                  keyid="showXAxis"
                  label={i18n.t("widget-types.line-chart.settings.show-x-axis")}
                  checked={widgetSettings.showXAxis}
                />
                {widgetSettings.showXAxis &&  
                  <SettingsSingleSelect
                    defaultValue={axisTickIntervalOptions[0]}
                    keyid="xTickInterval"
                    label={i18n.t("widget-types.line-chart.settings.tick-interval")}
                    options={axisTickIntervalOptions}
                    value={axisTickIntervalOptions.find((option) => option.value === widgetSettings.xTickInterval)}
                  />
                }
              </SettingsAccordion>

              {/* Y-Axis */}
              <SettingsAccordion label={i18n.t("widget-types.line-chart.settings.section-y-axis")} showBottomDivider={true}>
                <SettingsCheckbox
                  keyid="showYAxis"
                  label={i18n.t("widget-types.line-chart.settings.show-y-axis")}
                  checked={widgetSettings.showYAxis}
                />
                {widgetSettings.showYAxis &&
                  <>
                    <SettingsSingleSelect
                      defaultValue={yBaselineOptions[1]}
                      keyid="yBaseline"
                      label={i18n.t("widget-types.line-chart.settings.y-baseline")}
                      options={yBaselineOptions}
                      value={yBaselineOptions.find((option) => option.value === widgetSettings.yBaseline)}
                    />
                    <SettingsSingleSelect
                      defaultValue={axisTickIntervalOptions[0]}
                      keyid="yTickInterval"
                      label={i18n.t("widget-types.line-chart.settings.tick-interval")}
                      options={axisTickIntervalOptions}
                      value={axisTickIntervalOptions.find((option) => option.value === widgetSettings.yTickInterval)}
                    />
                  </>
                }
              </SettingsAccordion>

              {/* Widget Legend */}
              <SettingsLegend
                settingsState={widgetSettings ?? {}}
              />
            </SettingsOptions>
        </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  );
}